/** HTTP API of the game server (see LoginRequest/LoginResponse and StatsResponse in shared). */
import type { LoginRequest, LoginResponse, StatsResponse } from '@cube-rush/shared';

/** Why a login failed, from the player's point of view. */
export type LoginFailure =
  | 'wrong_password'
  | 'rate_limited'
  | 'bad_request'
  /** No answer: offline, server down, timeout or a proxy that cannot reach the server. */
  | 'unreachable'
  /** The server answered with an unexpected error. */
  | 'server';

export type LoginResult = { ok: true; session: LoginResponse } | { ok: false; error: LoginFailure };

export interface AuthApi {
  login(request: LoginRequest): Promise<LoginResult>;
}

export interface AuthApiOptions {
  fetch?: typeof fetch;
  /** Prefix for API paths; '' = same origin (the client is served by the game server). */
  baseUrl?: string;
  timeoutMs?: number;
}

export function createAuthApi(options: AuthApiOptions = {}): AuthApi {
  const fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
  const baseUrl = options.baseUrl ?? '';
  const timeoutMs = options.timeoutMs ?? 10_000;

  return {
    async login(request) {
      // A manual timer instead of AbortSignal.timeout(), which iOS 15 lacks.
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), timeoutMs);
      let response: Response;
      try {
        response = await fetchImpl(`${baseUrl}/api/login`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(request),
          signal: abort.signal,
        });
      } catch {
        return { ok: false, error: 'unreachable' };
      } finally {
        clearTimeout(timer);
      }
      if (response.ok) {
        const body = await readJson(response);
        return isLoginResponse(body) ? { ok: true, session: body } : { ok: false, error: 'server' };
      }
      return { ok: false, error: failureForStatus(response.status) };
    },
  };
}

function failureForStatus(status: number): LoginFailure {
  if (status === 401 || status === 403) return 'wrong_password';
  if (status === 429) return 'rate_limited';
  if (status === 400 || status === 422) return 'bad_request';
  // 502–504 come from a proxy (Vite in development, Caddy in production) that cannot reach us.
  if (status >= 502 && status <= 504) return 'unreachable';
  return 'server';
}

async function readJson(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function isLoginResponse(value: unknown): value is LoginResponse {
  if (typeof value !== 'object' || value === null) return false;
  const { token, playerId, expiresAt } = value as Record<string, unknown>;
  return (
    typeof token === 'string' &&
    token.length > 0 &&
    typeof playerId === 'string' &&
    playerId.length > 0 &&
    typeof expiresAt === 'number'
  );
}

/** Why the statistics did not come. */
export type StatsFailure = 'unauthorized' | 'unreachable' | 'server';

export type StatsResult = { ok: true; stats: StatsResponse } | { ok: false; error: StatsFailure };

export interface StatsApi {
  /** GET /api/stats with the session token. */
  stats(token: string): Promise<StatsResult>;
}

export function createStatsApi(options: AuthApiOptions = {}): StatsApi {
  const fetchImpl = options.fetch ?? globalThis.fetch.bind(globalThis);
  const baseUrl = options.baseUrl ?? '';
  const timeoutMs = options.timeoutMs ?? 10_000;

  return {
    async stats(token) {
      const abort = new AbortController();
      const timer = setTimeout(() => abort.abort(), timeoutMs);
      let response: Response;
      let body: unknown;
      try {
        response = await fetchImpl(`${baseUrl}/api/stats`, {
          headers: { authorization: `Bearer ${token}` },
          cache: 'no-store',
          signal: abort.signal,
        });
        body = response.ok ? await readJson(response) : null;
      } catch {
        return { ok: false, error: 'unreachable' };
      } finally {
        clearTimeout(timer);
      }
      if (response.status === 401) return { ok: false, error: 'unauthorized' };
      if (response.status >= 502 && response.status <= 504)
        return { ok: false, error: 'unreachable' };
      if (!response.ok || !isStatsResponse(body)) return { ok: false, error: 'server' };
      return { ok: true, stats: body };
    },
  };
}

function isStatsResponse(value: unknown): value is StatsResponse {
  if (typeof value !== 'object' || value === null) return false;
  const { players, family, now } = value as Record<string, unknown>;
  return (
    Array.isArray(players) &&
    typeof family === 'object' &&
    family !== null &&
    typeof now === 'number'
  );
}
